import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { StructureFix, StructureIssue } from '@peruselab/api-client/structureChecks';
import { Icon } from '../Icon';

const SEVERITY_ICON = { ERROR: 'error', WARNING: 'warning', INFO: 'info' } as const;
const SUPERSCRIPT = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹'
};

/** A charge as chemists write it: +1, −1 (a true minus sign). */
function signed(charge: string): string {
  const value = Number(charge);
  if (!Number.isFinite(value)) return charge;
  return value > 0 ? `+${value}` : value < 0 ? `−${Math.abs(value)}` : '0';
}

/** A charge as an ion's superscript: ⁺, ²⁺, ⁻. */
function superscript(charge: string): string {
  const value = Number(charge);
  if (!Number.isInteger(value) || value === 0) return '';
  const size =
    Math.abs(value) === 1 ? '' : [...String(Math.abs(value))].map((digit) => SUPERSCRIPT[digit as '0']).join('');
  return size + (value > 0 ? '⁺' : '⁻');
}

/** ADR 0020 D8 (#897): an issue's message, from its i18n key and arguments. */
export function structureIssueMessage(t: TFunction, issue: StructureIssue): string {
  const args = issue.args.charge === undefined ? issue.args : { ...issue.args, charge: signed(issue.args.charge) };
  return t(issue.messageKey, { ...args, defaultValue: issue.rule });
}

/** D6 (#1101): a fix's label, e.g. "Make it Na⁺". */
export function structureFixLabel(t: TFunction, fix: StructureFix): string {
  const args = fix.args.charge === undefined ? fix.args : { ...fix.args, charge: superscript(fix.args.charge) };
  return t(fix.messageKey, { ...args, defaultValue: fix.kind });
}

export interface StructureChecksActions {
  readonly onSelect: (issue: StructureIssue) => void;
  /** D6: a fix shown on the paper while hovered or focused; cancelled on leaving, applied on click. */
  readonly onPreviewFix?: (fix: StructureFix) => void;
  readonly onCancelFix?: () => void;
  readonly onApplyFix?: (fix: StructureFix) => void;
  /** D5: the author says the warning is intended, or takes it back. */
  readonly onConfirm?: (issue: StructureIssue) => void;
  readonly onWithdraw?: (issue: StructureIssue) => void;
}

/**
 * ADR 0020 D3 (#1099, owner: top of the Arrange tab, only when there are issues): what the server's
 * structure check found, errors first. Selecting an entry selects its atoms and bonds on the paper.
 * #1101: each entry's one-click fixes (D6), and on a warning the author's "It's intended" (D5).
 */
export function StructureChecksSection({
  issues,
  onSelect,
  onPreviewFix,
  onCancelFix,
  onApplyFix,
  onConfirm,
  onWithdraw
}: { readonly issues: readonly StructureIssue[] } & StructureChecksActions) {
  const { t } = useTranslation();
  if (issues.length === 0) return null;
  return (
    <section className="rail-section structure-checks" aria-labelledby="structure-checks-heading">
      <h2 id="structure-checks-heading">
        {t('structureCheck.heading')}{' '}
        <span className="structure-checks-count">{t('structureCheck.count', { count: issues.length })}</span>
      </h2>
      <p className="rail-hint">{t('structureCheck.hint')}</p>
      <ul className="structure-checks-list">
        {issues.map((issue, index) => {
          const message = structureIssueMessage(t, issue);
          const intended = issue.severity === 'WARNING' && issue.confirmation;
          return (
            <li key={`${issue.rule}-${index}`} className={intended ? 'structure-check-intended' : undefined}>
              <button
                type="button"
                className={`structure-check structure-check--${intended ? 'intended' : issue.severity.toLowerCase()}`}
                onClick={() => onSelect(issue)}
              >
                <Icon name={intended ? 'check' : SEVERITY_ICON[issue.severity]} />
                <span className="visually-hidden">{t(`structureCheck.severity.${issue.severity}`)}:</span>{' '}
                <span>{message}</span>
              </button>
              {intended ? (
                <p className="structure-check-confirmed">
                  {t(issue.confirmation!.byYou ? 'structureCheck.intendedByYou' : 'structureCheck.intendedByAuthor')}
                  {issue.canConfirm && onWithdraw ? (
                    <button type="button" className="structure-check-action" onClick={() => onWithdraw(issue)}>
                      {t('structureCheck.withdraw')}
                    </button>
                  ) : null}
                </p>
              ) : (issue.fixes?.length ?? 0) > 0 || (issue.severity === 'WARNING' && issue.canConfirm) ? (
                <div className="structure-check-actions">
                  {(issue.fixes ?? []).map((fix) => (
                    <button
                      key={fix.kind}
                      type="button"
                      className="structure-check-action"
                      onPointerEnter={() => onPreviewFix?.(fix)}
                      onPointerLeave={() => onCancelFix?.()}
                      onFocus={() => onPreviewFix?.(fix)}
                      onBlur={() => onCancelFix?.()}
                      onClick={() => onApplyFix?.(fix)}
                    >
                      <Icon name="check" />
                      <span>{structureFixLabel(t, fix)}</span>
                    </button>
                  ))}
                  {issue.severity === 'WARNING' && issue.canConfirm && onConfirm ? (
                    <button type="button" className="structure-check-action" onClick={() => onConfirm(issue)}>
                      {t('structureCheck.intended')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
